/**
 * Shared UI component for UI Form Field.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

import { UiFormFieldAppearance, UiFormFieldType } from './ui-form-field.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

@Component({
  selector: 'ui-form-field',
  standalone: true,
  imports: [InputTextModule, TextareaModule, TooltipModule, UiLabelTranslatePipe],
  templateUrl: './ui-form-field.component.html',
  styleUrl: './ui-form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiFormFieldComponent {
  readonly passwordVisible = signal(false);
  readonly dragOver = signal(false);
  readonly selectedFiles = signal<File[]>([]);

  readonly appearance = input<UiFormFieldAppearance>('outline');
  readonly label = input('');
  readonly value = input<string>('');
  readonly type = input<UiFormFieldType>('text');
  readonly placeholder = input('');
  readonly id = input('');
  readonly disabled = input(false);
  readonly required = input(false);
  readonly readonly = input(false);
  readonly error = input('');
  readonly hint = input('');
  readonly rows = input(3);
  readonly min = input<string>('');
  readonly max = input<string>('');
  readonly step = input<string>('');
  readonly showPasswordLabel = input('shared.form.showPassword');
  readonly hidePasswordLabel = input('shared.form.hidePassword');
  readonly accept = input<string>('');
  readonly multiple = input(false);
  readonly maxFileSizeMb = input<number>(0);
  readonly valueChange = output<string>();
  readonly filesChange = output<File[]>();

  readonly isPasswordField = computed(() => this.type() === 'password');
  readonly isFileField = computed(() => this.type() === 'file');
  readonly isPlaceholderLong = computed(() => this.placeholder().length > 20);
  readonly showPlaceholderHint = computed(() => this.isPlaceholderLong() && !this.isFileField());
  readonly resolvedInputType = computed(() => (this.isPasswordField() && this.passwordVisible() ? 'text' : this.type()));
  readonly passwordToggleLabel = computed(() => (this.passwordVisible() ? this.hidePasswordLabel() : this.showPasswordLabel()));
  readonly passwordToggleIcon = computed(() => (this.passwordVisible() ? 'visibility_off' : 'visibility'));
  readonly fileDropLabel = computed(() => this.placeholder() || 'shared.form.dropFiles');

  onValueChange(field: HTMLInputElement | HTMLTextAreaElement): void {
    const normalized = this.normalizeValue(field.value);

    if (field.value !== normalized) {
      field.value = normalized;
    }

    this.valueChange.emit(normalized);
  }

  togglePasswordVisibility(): void {
    if (!this.isPasswordField() || this.disabled()) {
      return;
    }

    this.passwordVisible.update((visible) => !visible);
  }

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.applyFiles(Array.from(input.files ?? []));
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    this.dragOver.set(true);
  }

  onDragLeave(): void {
    this.dragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    this.dragOver.set(false);
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.applyFiles(this.multiple() ? files : files.slice(0, 1));
  }

  removeFile(index: number): void {
    this.selectedFiles.update((files) => files.filter((_, i) => i !== index));
    this.filesChange.emit(this.selectedFiles());
  }

  private applyFiles(files: File[]): void {
    const maxBytes = this.maxFileSizeMb() * 1024 * 1024;
    const filtered = maxBytes > 0 ? files.filter((f) => f.size <= maxBytes) : files;
    this.selectedFiles.set(this.multiple() ? [...this.selectedFiles(), ...filtered] : filtered);
    this.filesChange.emit(this.selectedFiles());
  }

  private normalizeValue(value: string): string {
    if (this.type() === 'tel') {
      return value.replace(/\D+/g, '');
    }

    if (this.type() !== 'number') {
      return value;
    }

    const raw = value.replace(/[^0-9.-]/g, '');
    const hasLeadingMinus = raw.startsWith('-');
    const unsigned = hasLeadingMinus ? raw.slice(1) : raw;
    const [integerPart = '', ...decimalParts] = unsigned.split('.');
    const normalizedInteger = integerPart.replace(/-/g, '');
    const normalizedDecimal = decimalParts.join('').replace(/[.-]/g, '');
    const decimalSuffix = unsigned.includes('.') ? `.${normalizedDecimal}` : '';

    return `${hasLeadingMinus ? '-' : ''}${normalizedInteger}${decimalSuffix}`;
  }
}
